import { action, autorun, computed, observable, toJS } from "mobx";
import { FormStore } from "@modules/formx";
import { merge } from "lodash";
import { store } from "@store";
import { Navigation } from "@navigation";
import {
  Accommodation_Add,
  Accommodation_Edit,
  Accommodation_Details,
  AccommodationSearch_Search,
  GetUserSelectUsers
} from "@graphql";
import { FormData, initialValues, schema, oneOffer } from "./utils";
import { AccommodationProfileLocation } from "@navigation/locations";

export class AccommodationEditorStore {
  form: FormStore<FormData>;
  @observable loading = false;
  @observable img: File = null;
  @observable preview: string = null;
  @observable enabledUsers: LabelValue[] = [];

  constructor(
    public readonly navigation: Navigation,
    public readonly id?: string,
    initialData?: Partial<FormData>
  ) {
    this.form = new FormStore<FormData>({
      schema,
      onSubmit: this.onSubmit,
      initialValues
    });

    const _initialValues = merge({}, initialValues, initialData);

    if (id) {
      this.fetch(id, _initialValues);
    } else {
      this.form.reset(_initialValues);
      this.loading = false;
    }

    autorun(() => {
      GetUserSelectUsers.func({}).then((res) => {
        this.enabledUsers = res.data.users
          .filter((x) => x.Enabled)
          .map((x) => ({
            label: x.Name,
            value: x._id
          }));
      });
    });

    (window as any).accommodationEditorStore = this;
  }

  fetch = async (id: string, _initialValues: FormData) => {
    try {
      this.loading = true;
      await Accommodation_Details.func({ id }, { fetchPolicy: "network-only" }).then(
        (res) => {
          const {
            ResponsiblePersonRef,
            unfit, //removed from edit form
            ...values
          } = res?.data?.getAccommodationDetails || {};

          this.form.reset(
            merge({}, _initialValues, values, {
              ResponsiblePersonRef: ResponsiblePersonRef?.PersonId
            })
          );
        }
      );
    } catch (e) {
      store().snackbars.error(`Cannot fetch accommodation ${id}\nReason: ${e}`);
    } finally {
      this.loading = false;
    }
  };

  @computed get isNew() {
    return !this.id;
  }

  @action handleChangeChip = (value: string[], name: string) => {
    this.form.setValue(name, value);
  };

  get allCountries() {
    return store()
      .cache.allCountriesWorldwide.filter((c) => c.canWorkIn)
      .map((c) => c.name);
  }

  @action addOffer = () => {
    this.form.push("Offers", oneOffer);
    this.form.validate();
  };

  @action removeOffer = (index: number) => {
    this.form.removeByIndex("Offers", index);
    this.form.validate();
  };

  handleImg = () => {
    store()
      .upload.requestImageUploadAsUrl()
      .then(store().upload.requestImageCropWithPreview)
      .then(({ file, croppedFileURL }) => {
        if (file) {
          this.img = file;
          this.form.values.ImgUrl = croppedFileURL;
        }
      })
      .catch((e) => {
        store().snackbars.error(`Uploading img failed\nReason: ${e}`);
      });
  };

  @action onSubmit = async () => {
    const { ResponsiblePersonRef, _id, ImgUrl, Offers, ...values } = toJS(
      this.form.values
    );
    const offersNumCheck = Offers.map((x) => ({
      ...x,
      Price: Number(x.Price),
      Area: Number(x.Area),
      Rooms: Number(x.Rooms),
      Beds: Number(x.Beds)
    }));
    try {
      this.loading = true;
      if (this.id) {
        await Accommodation_Edit.func(
          {
            id: this.id,
            img: this.img,
            data: {
              Offers: offersNumCheck,
              ResponsiblePersonRef: { PersonId: ResponsiblePersonRef },
              ...values
            }
          },
          {
            refetchQueries: [AccommodationSearch_Search.OperationName]
          }
        ).then(() => {
          this.form.reset(values); // set form non-dirty
          this.navigation.unBlock("accommodation_form");
          this.navigation.navigate(new AccommodationProfileLocation(this.id));
        });
      } else {
        await Accommodation_Add.func(
          {
            responsiblePersonId: ResponsiblePersonRef,
            img: this.img,
            data: { Offers: offersNumCheck, ...values }
          },
          {
            refetchQueries: [AccommodationSearch_Search.OperationName]
          }
        ).then(({ data: { addAccommodation } }) => {
          this.form.reset(values); // set form non-dirty
          this.navigation.unBlock("accommodation_form");
          this.navigation.navigate(
            new AccommodationProfileLocation(addAccommodation?._id)
          );
        });
      }
    } catch (e) {
      store().snackbars.error(`Cannot save accommodation ${values.Name}\nReason: ${e}`);
    } finally {
      this.loading = false;
    }
  };
}
